import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Radar, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, fmtDate, notifyDeadlines } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { lavoro: "dipendente", eta: "", comune: "", isee: "", figli: 0, eta_figli: "", casa: "affitto", auto: false, bollo_mese: "", rca_scadenza: "", cie_scadenza: "", patente_scadenza: "", genitori_a_carico: false, disabilita_familiare: false, note: "" };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const CERT = { alta: "bg-emerald-50 text-emerald-700", media: "bg-amber-50 text-amber-700", bassa: "bg-slate-100 text-slate-600" };

const Sel = ({ label, value, onChange, items, id }) => (
  <div><Label>{label}</Label>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger data-testid={id}><SelectValue /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const BonusList = ({ res }) => (
  <div className="space-y-4" data-testid="bonus-results">
    {res.consiglio && <p className="rounded-2xl bg-[#1E3A8A]/5 p-4 text-[#1E3A8A]">{res.consiglio}</p>}
    {(res.bonus || []).map((b, i) => (
      <div key={i} className="rounded-3xl border bg-white p-6 lift" data-testid={`bonus-item-${i}`}>
        <div className="flex flex-wrap items-center gap-2"><h3 className="text-lg font-medium">{b.nome}</h3><span className={`text-xs rounded-full px-2.5 py-0.5 ${CERT[b.certezza] || CERT.bassa}`}>Probabilità {b.certezza}</span>{b.importo && <span className="text-xs rounded-full bg-[#E07A5F]/10 text-[#b5533a] px-2.5 py-0.5">{b.importo}</span>}</div>
        <p className="mt-2 text-slate-700">{b.perche}</p>
        {b.requisiti?.length > 0 && <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">{b.requisiti.map((r) => <li key={r}>{r}</li>)}</ul>}
        <p className="mt-3 text-sm"><b>Come:</b> {b.come}</p>
        <div className="mt-3 flex gap-4 text-sm">
          {b.link?.startsWith("https://") && <a href={b.link} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] flex items-center gap-1 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Sito ufficiale</a>}
          {b.guida && <Link to={`/app/guide/${b.guida}`} className="text-[#1E3A8A] hover:underline">Guida passo passo</Link>}
        </div>
      </div>
    ))}
    <p className="text-xs text-slate-500">Risultati orientativi generati da AI il {fmtDate(res.created_at)}: verifica sempre requisiti e scadenze sui siti ufficiali.</p>
  </div>
);

export default function FiscalRadar() {
  const [f, setF] = useState(EMPTY);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/fiscal/profile").then(({ data }) => data.user_id && setF({ ...EMPTY, ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ""])) }));
    api.get("/bonus/last").then(({ data }) => data.bonus && setRes(data));
  }, []);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    const n = (v) => (v === "" || v === null ? null : Number(v));
    await api.put("/fiscal/profile", { ...f, eta: n(f.eta), isee: n(f.isee), figli: Number(f.figli) || 0, bollo_mese: n(f.bollo_mese),
      rca_scadenza: f.rca_scadenza || null, cie_scadenza: f.cie_scadenza || null, patente_scadenza: f.patente_scadenza || null });
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      await save();
      if (kind === "cal") {
        const { data } = await api.post("/fiscal/generate");
        notifyDeadlines();
        toast.success(data.created ? `${data.created} scadenze aggiunte allo Scadenzario` : "Il tuo calendario è già aggiornato");
      } else {
        const { data } = await api.post("/bonus/radar");
        setRes(data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="radar-page">
      <PageHeader eyebrow="Radar bonus e calendario fiscale" title="Cosa ti spetta, e quando scade" desc="Rispondi a poche domande: creiamo tutte le scadenze dell'anno e cerchiamo i bonus a cui potresti avere diritto." />
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-3xl border bg-white p-6 space-y-4 h-fit">
          <Sel label="Lavoro" value={f.lavoro} onChange={set("lavoro")} id="fiscal-lavoro" items={[["dipendente", "Dipendente"], ["pensionato", "Pensionato"], ["partita_iva", "Partita IVA"], ["disoccupato", "Disoccupato"], ["studente", "Studente"]]} />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Età</Label><Input type="number" value={f.eta} onChange={set("eta")} data-testid="fiscal-eta" /></div>
            <div><Label>ISEE (se lo sai)</Label><Input type="number" value={f.isee} onChange={set("isee")} data-testid="fiscal-isee" /></div>
          </div>
          <div><Label>Comune</Label><Input value={f.comune} onChange={set("comune")} data-testid="fiscal-comune" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Figli a carico</Label><Input type="number" min="0" value={f.figli} onChange={set("figli")} data-testid="fiscal-figli" /></div>
            <div><Label>Età dei figli</Label><Input value={f.eta_figli} onChange={set("eta_figli")} placeholder="es. 2, 7" data-testid="fiscal-eta-figli" /></div>
          </div>
          <Sel label="Casa" value={f.casa} onChange={set("casa")} id="fiscal-casa" items={[["affitto", "In affitto"], ["prima_casa", "Solo prima casa di proprietà"], ["piu_immobili", "Più immobili / seconda casa"], ["altro", "Altro"]]} />
          <label className="flex items-center justify-between text-sm">Ho un'auto<Switch checked={!!f.auto} onCheckedChange={set("auto")} data-testid="fiscal-auto" /></label>
          {f.auto && (
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Mese scadenza bollo" value={f.bollo_mese || "0"} onChange={(v) => set("bollo_mese")(v === "0" ? "" : v)} id="fiscal-bollo" items={[["0", "Non so"], ...MESI.map((m, i) => [String(i + 1), m])]} />
              <div><Label>Scadenza RC auto</Label><Input type="date" value={f.rca_scadenza} onChange={set("rca_scadenza")} data-testid="fiscal-rca" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Scadenza carta d'identità</Label><Input type="date" value={f.cie_scadenza} onChange={set("cie_scadenza")} data-testid="fiscal-cie" /></div>
            <div><Label>Scadenza patente</Label><Input type="date" value={f.patente_scadenza} onChange={set("patente_scadenza")} data-testid="fiscal-patente" /></div>
          </div>
          <label className="flex items-center justify-between text-sm">Assisto un genitore anziano<Switch checked={!!f.genitori_a_carico} onCheckedChange={set("genitori_a_carico")} data-testid="fiscal-genitori" /></label>
          <label className="flex items-center justify-between text-sm">In famiglia c'è una persona con disabilità<Switch checked={!!f.disabilita_familiare} onCheckedChange={set("disabilita_familiare")} data-testid="fiscal-disabilita" /></label>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => run("cal")} disabled={!!busy} className="rounded-full" data-testid="fiscal-generate-btn">{busy === "cal" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-2" />} Crea il mio calendario fiscale</Button>
            <Button onClick={() => run("bonus")} disabled={!!busy} variant="outline" className="rounded-full" data-testid="bonus-radar-btn">{busy === "bonus" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Radar className="h-4 w-4 mr-2" />} Cerca i bonus per me</Button>
          </div>
        </div>
        <div className="lg:col-span-3">
          {busy === "bonus" && <div className="rounded-3xl border bg-white p-10 text-center text-slate-500"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#1E3A8A]" /><p className="mt-3">Sto controllando le agevolazioni…</p></div>}
          {!busy && res && <BonusList res={res} />}
          {!busy && !res && <div className="rounded-3xl border bg-white p-10 text-slate-500" data-testid="bonus-empty">Compila il profilo e premi "Cerca i bonus per me".</div>}
        </div>
      </div>
    </div>
  );
}
